import {Injectable} from '@angular/core';
import {AuthService} from "../../core/auth/auth.service";
import {BehaviorSubject, catchError, filter, map, Observable, of, shareReplay, tap} from "rxjs";
import {StorageService} from "@portal/shared/services/storage.service";
import {Org} from "@portal/api/responses/organizations/org-details-response";
import {OrganizationsService} from "@portal/api/organizations-service";
import {SearchRequest} from "@portal/api/requests/search-request";
import {Guid} from "guid-typescript";
import {PartySelectedService} from "@portal/shared/services/party-selected.service";
import { AppContextService } from '@portal/shared/services/app-context.service';

@Injectable({
    providedIn: 'root',
})
// responsible to manage what organization is selected
// and the current list of organizations of the user
export class OrganizationSelectorService {
    private readonly storageKey = 'org';
    private readonly orgListRequest: SearchRequest = {
        page: 0,
        pageSize: 100,
        sortDirection: 0,
    };

    private currentSelectedOrg = new BehaviorSubject<Org>(null);
    private currentUserList = new BehaviorSubject<Org[]>(null);
    private pendingOrgId: Guid | null = null;

    get org(): Org | null {
        return this.currentSelectedOrg.getValue();
    }

    get org$(): Observable<Org> {
        return this.currentSelectedOrg.asObservable().pipe(filter(item => item != null), shareReplay());
    }

    get orgState$(): Observable<Org | null> {
        return this.currentSelectedOrg.asObservable().pipe(shareReplay());
    }

    get orgList$(): Observable<Org[]> {
        return this.currentUserList.asObservable().pipe(filter(item => item != null), shareReplay());
    }

    constructor(private _organizationsService: OrganizationsService,
                private _storageService: StorageService,
                private _partySelectedService: PartySelectedService,
                private _authService: AuthService,
                private _appContextService: AppContextService) {
        this.loadOrgFromStorage();
        this.loadOrgList();
        this.listenToUserListChanges();
    }

    public loadOrgList() {
        if (this._authService.isAuthenticated) {
            this.loadUserOrgList();
        }
    }

    private listenToUserListChanges() {
        this.orgList$.subscribe(res => {
            if (this.pendingOrgId) {
                const match = res.find(userList => userList.id === this.pendingOrgId);
                if (match) {
                    this.pendingOrgId = null;
                    this.setSelectedOrg(match);
                    return;
                }
            }
            this.syncSelectedOrgWithList(res);
        });
    }

    private loadOrgFromStorage() {
        const org = this._storageService.getObject(this.storageKey);
        if (org) {
            if (!org.type) {
                this.pendingOrgId = org.id;
                this.refreshOrgList();
            } else {
                this.setSelectedOrg(org);
            }
        }
    }

    setSelectedOrg(org: Org) {
        const currentOrg = this.org;
        const isOrgChanged = !!currentOrg && org.id !== currentOrg.id;

        if (isOrgChanged) {
            this._storageService.remove('party');
            this._storageService.remove('partySettings');
            this._partySelectedService.resetParty();
        }

        if (org.id !== currentOrg?.id || currentOrg === null || org.type !== currentOrg.type || org.name !== currentOrg.name) {
            this._storageService.setOrganization(org.id, org.name, org.type);
            this.currentSelectedOrg.next(org);
        }
    }

    private loadUserOrgList() {
        if (this._authService.isAuthenticated) {
            this._organizationsService.listUserOrganizations(this.orgListRequest)
                .subscribe(res => {
                    this.currentUserList.next(res.data);
                });
        }
    }

    loadOrgList$() {
        return this._organizationsService.listUserOrganizations(this.orgListRequest).pipe(tap(res => {
            this.currentUserList.next(res.data);
        }));
    }

    ensureSelectedOrg$(): Observable<Org | null> {
        if (!this._authService.isAuthenticated) {
            return of(null);
        }

        const currentUserList = this.currentUserList.getValue();
        if (currentUserList !== null) {
            return of(this.syncSelectedOrgWithList(currentUserList));
        }

        return this.loadOrgList$().pipe(
            map(res => this.syncSelectedOrgWithList(res.data)),
            catchError(() => of(this.org))
        );
    }

    refreshOrgList() {
        this.loadUserOrgList();
    }

    hasOrgSelected(): boolean {
        return !!this.currentSelectedOrg.getValue();
    }

    resetSelectedOrg() {
        this.currentSelectedOrg.next(null);
    }

    private syncSelectedOrgWithList(orgList: Org[]): Org | null {
        const currentOrg = this.currentSelectedOrg.getValue();
        const preferredOrg = this.getPreferredOrgForContext(orgList);

        if (!orgList.length) {
            this.clearSelectedOrg();
            return null;
        }

        if (!currentOrg) {
            const nextOrg = preferredOrg ?? orgList[0];
            this.setSelectedOrg(nextOrg);
            return nextOrg;
        }

        const selectedOrg = orgList.find(userList => userList.id === currentOrg.id);
        if (selectedOrg && this.isOrgAllowedInCurrentContext(selectedOrg)) {
            this.setSelectedOrg(selectedOrg);
            return selectedOrg;
        }

        if (preferredOrg) {
            this.setSelectedOrg(preferredOrg);
            return preferredOrg;
        }

        if (selectedOrg) {
            this.setSelectedOrg(selectedOrg);
            return selectedOrg;
        }

        this.setSelectedOrg(orgList[0]);
        return orgList[0];
    }

    private clearSelectedOrg() {
        this._storageService.remove(this.storageKey);
        this._storageService.remove('party');
        this._storageService.remove('partySettings');
        this._partySelectedService.resetParty();
        this.currentSelectedOrg.next(null);
    }

    private getPreferredOrgForContext(orgList: Org[]): Org | null {
        try {
            return orgList.find(org => this.isOrgAllowedInCurrentContext(org)) ?? null;
        } catch {
            return null;
        }
    }

    private isOrgAllowedInCurrentContext(org: Org): boolean {
        return this._appContextService.isOrgTypeAllowed(org?.type);
    }
}
